/**
 * Razčlenjevanje niza `Izvajalec - Naslov` (T3 §3).
 *
 * Čista funkcija in prvi testni sloj (T1 §12): niz pride iz šestdesettisoč
 * postaj, ki nimajo skupne konvencije, zato je pokritost s testi edini način,
 * da se popravek za eno postajo ne pokvari drugi.
 *
 * ⚠️ Znana napaka, ki jo ta modul odpravlja: naivna izvedba vzame `parts[0]`
 * za izvajalca, naslov pa pusti kot **cel** izvirni niz. Prikaz je potem
 * »Queen — Queen - Bohemian Rhapsody«. Zato tu naslov vedno nastane iz
 * PREOSTANKA za ločilom, nikoli iz celote.
 *
 * Izvirni niz se hrani v `raw`: če razčlenjevalnik kdaj popravimo, se da
 * zgodovino znova obdelati (T2 §2, `HistoryEntry.raw`).
 */

import { artKey } from './artwork';

export interface Track {
  /** `null`, kadar postaja pošlje samo naslov — pogosto stanje, ne napaka. */
  artist: string | null;
  title: string;
  /** Niz, kot ga je poslala postaja, po čiščenju krmilnih znakov. */
  raw: string;
}

/**
 * Ločilo med izvajalcem in naslovom.
 *
 * Vezaj zahteva presledek na OBEH straneh — sicer bi »Jean-Michel Jarre«,
 * »Blink-182« in »Jay-Z« razpadli na izvajalca in naslov. Pomišljaja `–` in
 * `—` presledkov ne zahtevata: v imenih se praktično ne pojavljata.
 */
const SEPARATOR = / - | ?[–—] ?/;

/**
 * Krmilni znaki in NUL polnilo iz ICY bloka.
 *
 * Blok je poravnan na 16 bajtov in ostanek je zapolnjen z NUL; brez tega
 * bi vsak naslov nosil rep neizpisljivih znakov.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: NUL polnilo ICY bloka je natanko to, kar tu čistimo.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

/**
 * Nizi, ki jih postaje pošljejo namesto skladbe.
 *
 * Prazen prikaz je boljši od »Unknown«: uporabnik ne dobi lažnega vtisa, da
 * je nekaj razpoznano. Primerja se cel niz, ne podniz — »Unknown Mortal
 * Orchestra« je prava skupina.
 */
const PLACEHOLDERS = new Set([
  '',
  '-',
  '--',
  '---',
  'n/a',
  'na',
  'null',
  'undefined',
  'unknown',
  'unknown artist',
  'unknown - unknown',
  'no title',
  'notitle',
  'live stream',
  'livestream',
]);

function clean(value: string): string {
  return value.replace(CONTROL_CHARS, ' ').replace(/\s+/g, ' ').trim();
}

function isPlaceholder(value: string): boolean {
  return PLACEHOLDERS.has(value.toLowerCase());
}

/**
 * Odstrani izvajalca, kadar ga postaja ponovi še v naslovu.
 *
 * »Nirvana - Nirvana - Lithium« je pogost izpis postaj, ki ime izvajalca
 * lepijo na oba dela zapisa.
 *
 * ⚠️ Za imenom mora stati **ločilo**, ne le presledek. Brez tega pogoja bi
 * »Queen - Queen Rock Montreal« izgubil polovico naslova.
 */
function stripRepeatedArtist(artist: string, title: string): string {
  if (!title.toLowerCase().startsWith(artist.toLowerCase())) return title;

  const rest = title.slice(artist.length);
  const match = SEPARATOR.exec(rest);
  if (match === null || match.index !== 0) return title;

  const stripped = rest.slice(match[0].length).trim();
  return stripped === '' ? title : stripped;
}

/**
 * Razčleni niz iz `StreamTitle` ali iz JSON polja postaje.
 *
 * Vrne `null`, kadar ni česa pokazati — kar je normalno stanje in ne napaka:
 * govorne postaje in marsikateri Icecast pošiljajo samo ime programa.
 */
export function parseStreamTitle(raw: string): Track | null {
  const value = clean(raw);
  if (isPlaceholder(value)) return null;

  // Viseče ločilo na začetku ali koncu pomeni, da je postaja eno stran pustila
  // prazno. V `raw` ostane, ker je to dejansko poslani niz (T2 §2).
  const text = value.replace(/^[-–—]+\s*/, '').replace(/\s*[-–—]+$/, '');
  if (isPlaceholder(text)) return null;

  const at = text.search(SEPARATOR);
  if (at === -1) return { artist: null, title: text, raw: value };

  const separator = SEPARATOR.exec(text.slice(at))?.[0] ?? ' - ';
  const artist = text.slice(0, at).trim();
  const title = text.slice(at + separator.length).trim();

  // »Artist - « in » - Title«: eno stran je postaja pustila prazno. Ostanek je
  // naslov, ne izvajalec — izvajalec brez naslova ni podatek, ki bi ga kazali.
  if (artist === '' || isPlaceholder(artist)) {
    return title === '' ? null : { artist: null, title, raw: value };
  }
  if (title === '' || isPlaceholder(title)) {
    return { artist: null, title: artist, raw: value };
  }

  return { artist, title: stripRepeatedArtist(artist, title), raw: value };
}

/**
 * Ključ za predpomnilnik ovitkov (T4 §6).
 *
 * Poslušalci iste postaje slišijo isto skladbo hkrati, zato mora ključ
 * prenesti razlike v ločilih, velikih črkah in šumnikih — sicer se
 * »Siddharta - Ledena« in »SIDDHARTA – Ledená« ne srečata v KV.
 *
 * ⚠️ Ločila v ključu **ni**: normalizacija ga pobriše. Ključ, sestavljen z
 * vezajem PO normalizaciji, se z Workerjevim ne bi ujemal — predpomnilnik bi
 * se tiho razdelil na dva niza ključev in razpolovil zadetke. Prav to se je
 * zgodilo, dokler sta bili izvedbi dve; zdaj je normalizacija ena sama
 * (`domain/artwork.ts`) in test to še vedno čuva.
 */
export function trackKey(track: Track): string {
  const title = artKey(track.title);
  const artist = track.artist === null ? '' : artKey(track.artist);
  return artist === '' ? title : `${artist} ${title}`;
}

/**
 * Ali gre za isto skladbo.
 *
 * Primerja se ključ, ne izvirni niz: postaje isto skladbo v zaporednih
 * blokih pogosto pošljejo z drugačnimi presledki ali velikimi črkami, in
 * vsaka taka razlika bi po nepotrebnem oglasila živo regijo (P2 §3).
 */
export function sameTrack(a: Track | null, b: Track | null): boolean {
  if (a === null || b === null) return a === b;
  return trackKey(a) === trackKey(b);
}
